document.addEventListener('DOMContentLoaded', () => {
  const STATUS_ERROR = window.opentelemetry?.api?.SpanStatusCode?.ERROR ?? 2;

  function getTracer() {
    if (window.modshopTracer) return window.modshopTracer;
    if (window.opentelemetry?.api?.trace?.getTracer) {
      return window.opentelemetry.api.trace.getTracer('cart-flows');
    }
    return undefined;
  }

  async function resolveDirectImageUrl(imageUrl, signal) {
    if (!imageUrl || imageUrl.includes('.png')) {
      return imageUrl;
    }

    if (!imageUrl.includes('/view/')) {
      return imageUrl;
    }

    try {
      const response = await fetch(imageUrl, {
        method: 'GET',
        redirect: 'follow',
        cache: 'no-store',
        signal
      });
      const resolvedImageUrl = response.url || imageUrl;

      if (response.body?.cancel) {
        response.body.cancel().catch(() => {});
      }

      return resolvedImageUrl;
    } catch (error) {
      return imageUrl;
    }
  }

  async function uploadShopifyImage(productImage) {
    const modshopSettings = window.mshopSettings || window.shopSettings || {};
    const adminAppApiUrlValue = modshopSettings.adminappapiurl || modshopSettings.adminAppApiUrl;
    const adminAppApiKey = modshopSettings.adminappapikey || modshopSettings.adminAppApiKey;
    const adminAppApiUrl = adminAppApiUrlValue
      ? adminAppApiUrlValue.replace(/\/+$/, '').endsWith('/admin-actions')
        ? adminAppApiUrlValue.replace(/\/+$/, '')
        : `${adminAppApiUrlValue.replace(/\/+$/, '')}/admin-actions`
      : '';

    if (!productImage || !adminAppApiUrl || !adminAppApiKey) {
      return productImage;
    }

    const timeoutMs = 15000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const directImageUrl = await resolveDirectImageUrl(productImage, controller.signal);

      // Determine siteID based on region from entry-point-data
      const entryPointData = document.getElementById('entry-point-data');
      const region = entryPointData?.getAttribute('data-region') || 'US';
      let siteID = 'FENDER_EMEA'; // default/catch-all for all EMEA locales

      if (region === 'US') {
        siteID = 'FENDER_US';
      } else if (region === 'AU') {
        siteID = 'FENDER_AU';
      } else if (region === 'JP') {
        siteID = 'FENDER_JP';
      }

      const uploadPayload = {
        requestType: 'uploadShopifyImage',
        siteID: siteID,
        data: {
          imageUrl: directImageUrl,
          alt: '',
          contentType: 'IMAGE'
        }
      };

      const response = await fetch(adminAppApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': adminAppApiKey
        },
        body: JSON.stringify(uploadPayload),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`uploadShopifyImage ${response.status}`);
      }

      const responseData = await response.json();
      const uploadedImageUrl =
        responseData?.data?.imageUrl ||
        responseData?.data?.files?.[0]?.image?.url ||
        responseData?.data?.files?.[0]?.url;

      return uploadedImageUrl || productImage;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error('uploadShopifyImage timed out after 10000ms');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  function isShopifyCdnUrl(url) {
    if (!url) return false;
    try {
      const parsed = new URL(url);
      return parsed.hostname === 'cdn.shopify.com' || parsed.hostname.endsWith('.shopifycdn.com');
    } catch (error) {
      return false;
    }
  }

  async function removeModShopItemByRecipeId(recipeId) {
    const cart = await fetch('/cart.js').then(res => res.json());

    const item = cart.items.find(
      item => item.properties?._modshoprecipeid === recipeId || item.modshoprecipeid === recipeId
    );

    if (!item) return;

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates: {
          [item.key]: 0
        }
      })
    });
  }

  function extractTextFromRichText(richTextData) {
    if (!richTextData) return '';

    try {
      const data = typeof richTextData === 'string' ? JSON.parse(richTextData) : richTextData;

      function extractText(node) {
        if (!node) return '';

        if (node.type === 'text' && node.value) {
          return node.value;
        }

        if (node.children && Array.isArray(node.children)) {
          return node.children.map(child => extractText(child)).join('');
        }

        return '';
      }

      return extractText(data).trim();
    } catch (error) {
      return '';
    }
  }

  function transformFullSpectrumPayload(fullPayload) {
    if (!fullPayload || !fullPayload.items || !Array.isArray(fullPayload.items)) {
      return null;
    }

    function categoryToFieldName(category) {
      return category
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    }

    function extractBaseProductSku(items) {
      const lastItem = items[items.length - 1];
      if (lastItem && !lastItem.sku.startsWith('fen-sel-')) {
        return lastItem.sku;
      }

      for (let i = items.length - 1; i >= 0; i--) {
        if (/^\d+$/.test(items[i].sku)) {
          return items[i].sku;
        }
      }

      return '';
    }

    function extractPlatform(items) {
      for (const item of items) {
        const match = item.sku.match(
          /fen-sel-(?:guitar|bass)-([a-z-]+?)-(orientation|body|neck|fingerboard|pickups|pickguard|plastic|tuning|bridge|hardware|strap|strings|case)/
        );
        if (match && match[1]) {
          return match[1];
        }
      }
      return '';
    }

    const config = {
      recipe_set_id: fullPayload.recipeSetId || '',
      base_product_sku: extractBaseProductSku(fullPayload.items),
      platform: extractPlatform(fullPayload.items),
      price: fullPayload.options?.finalPrice || 0,
      currency: fullPayload.options?.isoCurrency || 'USD'
    };

    const configurableItems = fullPayload.items.filter(item => item?.sku?.startsWith('fen-sel-'));
    const userSelections = Array.isArray(fullPayload.options?.userSelections) ? fullPayload.options.userSelections : [];

    userSelections.forEach((selection, index) => {
      if (typeof selection !== 'string' || !selection.includes(' - ')) {
        return;
      }

      const item = configurableItems[index];
      const firstDashIndex = selection.indexOf(' - ');
      if (firstDashIndex === -1) {
        return;
      }

      const category = selection.substring(0, firstDashIndex).trim();
      const value = selection.substring(firstDashIndex + 3).trim();

      let fieldName = categoryToFieldName(category);

      if (fieldName === 'hardware') {
        if (item?.sku?.includes('-hardware-color-')) {
          fieldName = 'hardware_finish';
        } else if (item?.sku?.includes('-strap-locks-')) {
          fieldName = 'hardware_straplocks';
        }
      }

      config[fieldName] = value;
    });

    if (fullPayload.thumbnailsByAngle && fullPayload.primaryThumbnailAngle) {
      const primaryThumbnail = fullPayload.thumbnailsByAngle.find(t => t.angle === fullPayload.primaryThumbnailAngle);
      if (primaryThumbnail && primaryThumbnail.url) {
        config.image_url = primaryThumbnail.url;
      }
    }

    return config;
  }

  async function submitModShopProductForm(productPrice, recipeSetId, productImage, variantID, fullPayload = {}) {
    window.modshopSetBusy?.(true);
    console.log('full spectrum payload', fullPayload);
    const tracer = getTracer();
    const span = tracer?.startSpan('modshop_add_to_cart', {
      attributes: {
        component: 'cart-flows',
        fullPayload: JSON.stringify(fullPayload)
      }
    });
    let success = false;
    let isLefty = false;

    if (fullPayload?.items[0].name.toLowerCase().includes('left')) {
      isLefty = true;
    }

    // Extract cart message from variant metafield
    let cartMessage = '';
    try {
      const variantData = window.productData?.variants?.find(v => v.id == variantID);
      if (variantData?.metafields?.custom?.cart_message) {
        cartMessage = extractTextFromRichText(variantData.metafields.custom.cart_message);
        if (window.shopSettings?.environment !== 'prod') {
          console.log('[ModShop] Cart message extracted:', cartMessage);
        }
      }
    } catch (err) {
      span?.addEvent?.('cart_message_extraction_failed', { message: err.message });
      if (window.shopSettings?.environment !== 'prod') {
        console.error('[ModShop] Cart message extraction error:', err);
      }
    }

    const specs = fullPayload?.items.map(item => {
      return item.name;
    });

    const domain = window.location.hostname.toLowerCase();
    const scriptSrc = domain.includes('garage')
      ? 'https://staging-client.spectrumcustomizer.com/fender/consumer-specification/recipeId/locale/actualLocale/html'
      : 'https://client.spectrumcustomizer.com/fender/consumer-specification/recipeId/locale/actualLocale/html';

    try {
      const missing = [];
      if (!productPrice) missing.push('productPrice');
      if (!recipeSetId) missing.push('recipeSetId');
      if (!productImage) missing.push('productImage');
      if (!variantID) missing.push('variantID');

      if (missing.length) {
        span?.setStatus?.({ code: STATUS_ERROR, message: 'validation failed' });
        span?.addEvent?.('validation_failed', { missing });
        throw new Error(`Missing: ${missing.join(', ')}`);
      }

      const config = document.getElementById('spectrum-sku');
      const locale = config.getAttribute('data-locale') || 'en-US';
      let uploadedProductImage = productImage;
      let shopifyThumbnailUrl = '';

      try {
        uploadedProductImage = await uploadShopifyImage(productImage);
        if (isShopifyCdnUrl(uploadedProductImage)) {
          shopifyThumbnailUrl = uploadedProductImage;
          span?.addEvent?.('upload_shopify_image_success', { uploadedProductImage });
        } else {
          span?.addEvent?.('upload_shopify_image_non_shopify_url', { uploadedProductImage });
        }
      } catch (uploadErr) {
        span?.addEvent?.('upload_shopify_image_failed', { message: uploadErr.message });
        if (window.shopSettings?.environment !== 'prod') {
          console.error(uploadErr);
        }
      }

      const modshopConfiguration = transformFullSpectrumPayload(fullPayload);

      if (window.shopSettings?.environment !== 'prod') {
        console.log('modshop_configuration', modshopConfiguration);
      }

      let formData = {
        items: [
          {
            id: variantID,
            quantity: 1,
            properties: {
              _modshop_price: productPrice,
              _modshoprecipeid: recipeSetId,
              ...(shopifyThumbnailUrl && { _thumbnailUrl: shopifyThumbnailUrl }),
              _custom_image: productImage,
              _modshop_lefty: isLefty ? 'Yes' : 'No',
              _modshop_specs: specs.join(','),
              _modshop_edit_url: `${window.location.href.split('?')[0]}?recipeId=${recipeSetId}&edit=true`,
              _modshop_spec_sheet: `${scriptSrc.replace('recipeId', recipeSetId).replace('actualLocale', locale)}`,
              ...(cartMessage && { _modshop_cart_message: cartMessage }),
              ...(modshopConfiguration && { _modshopConfiguration: JSON.stringify(modshopConfiguration) })
            }
          }
        ],
        attributes: {} // Add attributes object
      };

      // Get values from localStorage and browser
      const srcCode = localStorage.getItem('srcCode');
      let anonymousID = localStorage.getItem('ajs_anonymous_id');
      if (anonymousID && anonymousID.startsWith('"') && anonymousID.endsWith('"')) {
        anonymousID = anonymousID.slice(1, -1);
      }
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userAgent = navigator.userAgent;
      const analyticsSessionId = localStorage.getItem('analytics_session_id') || '';
      const referrer = localStorage.getItem('shopify_referrer') || null;

      // Add attributes to formData
      formData.attributes = {
        user_agent: userAgent,
        time_zone: timeZone,
        analytics_session_id: analyticsSessionId,
        ...(referrer && { referrer }),
        ...(srcCode && { source_code: srcCode }),
        ...(anonymousID && { anonymous_id: anonymousID })
      };

      const params = new URLSearchParams(window.location.search);

      const isEditMode = params.get('edit') === 'true';
      const recipeId = params.get('recipeId');

      if (isEditMode && recipeId) {
        removeModShopItemByRecipeId(recipeId).catch(err => {
          console.error('Error removing existing item by recipe ID:', err);
        });
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        span?.setStatus?.({ code: STATUS_ERROR, message: `Shopify ${res.status}` });
        throw new Error(`Shopify ${res.status}`);
      }

      const data = await res.json();
      span?.addEvent?.('shopify_success');

      if (window.shopSettings?.environment !== 'prod') {
        console.info('Product added:', data);
      }

      success = true;

      try {
        const response = data;
        response.items_added = response.items;

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'modshop-product-form',
          productVariantId: variantID,
          cartData: response,
          spectrumData: fullPayload,
          modshopConfiguration: modshopConfiguration
        });
      } catch (innerErr) {
        console.error('Error in inner try block:', innerErr);
      }
    } catch (err) {
      span?.recordException?.(err);
      if (window.shopSettings?.environment !== 'prod') {
        console.error(err);
      }
    } finally {
      if (!window.modshopKeepBusyUntilNavigation) {
        window.modshopSetBusy?.(false);
      }
      span?.end?.();
    }

    return success;
  }

  window.submitModShopProductForm = submitModShopProductForm;
});
